<template>
  <div class="me-page">
    <AppHeader />

    <div class="page-body">
      <!-- 页面标题 -->
      <div class="page-heading">
        <div class="page-title">{{ $t("mePage.pageTitle") }}</div>
        <div class="page-desc">{{ $t("mePage.pageDesc") }}</div>
      </div>

      <!-- 未登录提示 -->
      <div v-if="!isLoggedIn" class="section-card">
        <div class="section-head">
          <div>
            <div class="section-title">{{ $t("mePage.loginTitle") }}</div>
            <div class="section-desc">{{ $t("mePage.loginDesc") }}</div>
          </div>
          <div class="section-badge">{{ $t("mePage.loginBadge") }}</div>
        </div>
      </div>

      <!-- 已登录但加载失败 -->
      <div v-if="isLoggedIn && errorMsg && !loading" class="section-card">
        <div class="section-head">
          <div>
            <div class="section-title">{{ $t("mePage.loadFailTitle") }}</div>
            <div class="section-desc">{{ errorMsg }}</div>
          </div>
          <div class="section-badge">{{ $t("mePage.errorBadge") }}</div>
        </div>

        <div class="invite-actions" style="margin-top: 16px;">
          <div class="action-btn primary" @click="loadMeHome(true)">{{ $t("mePage.reload") }}</div>
        </div>
      </div>

      <!-- 我的身份总览 -->
      <div class="profile-card">
        <div class="profile-bg profile-grid"></div>
        <div class="profile-bg profile-orb profile-orb-1"></div>
        <div class="profile-bg profile-orb profile-orb-2"></div>

        <div class="profile-top">
          <div class="profile-user">
            <div class="profile-avatar">{{ profileFirstLetter }}</div>
            <div class="profile-user-main">
              <div class="profile-name">
                {{ loading && !loaded ? '--' : walletAddressShort }}
              </div>
              <div class="profile-subtitle">{{ $t("mePage.walletIdentity") }}</div>
            </div>
          </div>
          <div class="profile-level">
            {{ loading && !loaded ? '--' : user.level || '--' }}
          </div>
        </div>

        <div class="profile-main">
          <div class="profile-main-label">{{ $t("mePage.currentRate") }}</div>
          <div class="profile-main-value">
            {{ loading && !loaded ? '--' : user.usdtRate || '--' }}
          </div>
          <div class="profile-main-unit">{{ $t("mePage.usdtRateUnit") }}</div>
        </div>

        <div class="profile-grid-info">
          <div class="profile-info-item">
            <span>{{ $t("mePage.directReward") }}</span>
            <strong>{{ loading && !loaded ? '--' : user.directEniRate || '--' }}</strong>
          </div>
          <div class="profile-info-item">
            <span>{{ $t("mePage.poolQualification") }}</span>
            <strong>
              {{
                loading && !loaded
                  ? '--'
                  : user.poolQualified
                    ? $t("mePage.obtained")
                    : $t("mePage.notObtained")
              }}
            </strong>
          </div>
        </div>
      </div>

      <!-- 推荐中心 -->
      <div class="section-card">
        <div class="section-head">
          <div>
            <div class="section-title">{{ $t("mePage.inviteCenterTitle") }}</div>
            <div class="section-desc">{{ $t("mePage.inviteCenterDesc") }}</div>
          </div>
          <div class="section-badge">{{ $t("mePage.inviteBadge") }}</div>
        </div>

        <div class="invite-panel">
          <div class="invite-panel-label">{{ $t("mePage.inviteLinkLabel") }}</div>

          <div class="invite-link-box">
            {{ loading && !loaded ? '--' : inviteLinkDisplay }}
          </div>

          <div class="invite-actions dual">
            <div class="action-btn primary" :class="{ disabled: !inviteLink }" @click="copyInviteLink">
              {{ $t("mePage.copyLink") }}
            </div>
            <div class="action-btn ghost" :class="{ disabled: !inviteCode }" @click="copyInviteCode">
              {{ $t("mePage.copyInviteCode") }}
            </div>
          </div>

          <div class="invite-meta">
            <div class="invite-pill">
              {{ $t("mePage.inviteCodeLabel") }}：{{ loading && !loaded ? '--' : inviteCodeShort || '--' }}
            </div>
            <div class="invite-pill">{{ $t("mePage.inviteMetaTip") }}</div>
          </div>
        </div>
      </div>

      <!-- ENI 地址绑定 -->
      <div class="section-card">
        <div class="section-head">
          <div>
            <div class="section-title">{{ $t("mePage.eniBindTitle") }}</div>
            <div class="section-desc">{{ $t("mePage.eniBindDesc") }}</div>
          </div>
          <div class="section-badge gold-badge">ENI</div>
        </div>

        <div class="bind-card">
          <div class="bind-status-row">
            <div>
              <div class="bind-label">{{ $t("mePage.bindStatusLabel") }}</div>
              <div class="bind-status"
                :class="(loading && !loaded) ? 'pending' : (user.eniAddressBound ? 'success' : 'pending')">
                {{
                  loading && !loaded
                    ? $t("mePage.loadingStatus")
                    : user.eniAddressBound
                      ? $t("mePage.boundStatus")
                      : $t("mePage.unboundStatus")
                }}
              </div>
            </div>
            <div class="bind-tip">
              {{
                loading && !loaded
                  ? $t("mePage.loadingBindStatus")
                  : user.eniAddressBound
                    ? $t("mePage.bindTipBound")
                    : $t("mePage.bindTipUnbound")
              }}
            </div>
          </div>

          <div class="bind-address-box">
            <div class="bind-address-label">{{ $t("mePage.currentEniAddress") }}</div>
            <div class="bind-address-value">
              {{
                loading && !loaded
                  ? '--'
                  : user.eniAddressBound
                    ? user.eniAddress
                    : $t("mePage.noBoundAddress")
              }}
            </div>
          </div>

          <div class="bind-actions">
            <div class="action-btn ghost" :class="{ disabled: !user.eniAddressBound }"
              v-if="loading || user.eniAddressBound" @click="copyBoundEniAddress">
              {{ $t("mePage.copyAddress") }}
            </div>
            <div class="action-btn primary" :class="{ disabled: !isLoggedIn || savingEni }"
              @click="handleBindEniAddress">
              {{
                savingEni
                  ? $t("mePage.saving")
                  : (user.eniAddressBound ? $t("mePage.modifyEniAddress") : $t("mePage.bindEniAddress"))
              }}
            </div>
          </div>
        </div>
      </div>

      <!-- 我的收益资格 -->
      <div class="section-card">
        <div class="section-head">
          <div>
            <div class="section-title">{{ $t("mePage.qualificationTitle") }}</div>
            <div class="section-desc">{{ $t("mePage.qualificationDesc") }}</div>
          </div>
          <div class="section-badge">
            {{ loading && !loaded ? '--' : user.level || '--' }}
          </div>
        </div>

        <div class="qualification-grid">
          <div class="qualification-item">
            <span>{{ $t("mePage.currentLevel") }}</span>
            <strong>{{ loading && !loaded ? '--' : user.level || '--' }}</strong>
          </div>
          <div class="qualification-item">
            <span>{{ $t("mePage.usdtRateLabel") }}</span>
            <strong>{{ loading && !loaded ? '--' : user.usdtRate || '--' }}</strong>
          </div>
          <div class="qualification-item">
            <span>{{ $t("mePage.directEniRate") }}</span>
            <strong>{{ loading && !loaded ? '--' : user.directEniRate || '--' }}</strong>
          </div>
          <div class="qualification-item">
            <span>{{ $t("mePage.poolQualification") }}</span>
            <strong>
              {{
                loading && !loaded
                  ? '--'
                  : user.poolQualified
                    ? $t("mePage.poolQualifiedText")
                    : $t("mePage.notObtained")
              }}
            </strong>
          </div>
          <div class="qualification-item">
            <span>{{ $t("mePage.sameLevelReward") }}</span>
            <strong>{{ loading && !loaded ? '--' : user.sameLevelReward || '--' }}</strong>
          </div>
          <div class="qualification-item">
            <span>{{ $t("mePage.overReward") }}</span>
            <strong>{{ loading && !loaded ? '--' : user.overReward || '--' }}</strong>
          </div>
        </div>
      </div>

      <!-- 我的账户信息 -->
      <div class="section-card">
        <div class="section-head">
          <div>
            <div class="section-title">{{ $t("mePage.accountInfoTitle") }}</div>
            <div class="section-desc">{{ $t("mePage.accountInfoDesc") }}</div>
          </div>
          <div class="section-badge">{{ $t("mePage.accountBadge") }}</div>
        </div>

        <div class="account-list">
          <div class="account-item account-item-wide">
            <span>{{ $t("mePage.currentWallet") }}</span>
            <strong>{{ walletAddress || '--' }}</strong>
          </div>

          <div class="account-item">
            <span>{{ $t("mePage.currentLevel") }}</span>
            <strong>{{ loading && !loaded ? '--' : user.level || '--' }}</strong>
          </div>

          <div class="account-item">
            <span>{{ $t("mePage.boundEniAddressShort") }}</span>
            <strong>
              {{
                loading && !loaded
                  ? '--'
                  : user.eniAddressBound
                    ? shortAddress(user.eniAddress)
                    : $t("mePage.unboundStatus")
              }}
            </strong>
          </div>

          <div class="account-item">
            <span>{{ $t("mePage.directCount") }}</span>
            <strong>{{ loading && !loaded ? '--' : `${formatInt(user.directCount)} ${$t("mePage.peopleUnit")}` }}</strong>
          </div>

          <div class="account-item">
            <span>{{ $t("mePage.teamCount") }}</span>
            <strong>{{ loading && !loaded ? '--' : `${formatInt(user.teamCount)} ${$t("mePage.peopleUnit")}` }}</strong>
          </div>

          <div class="account-item">
            <span>{{ $t("mePage.inviteStatus") }}</span>
            <strong>{{ walletAddress ? $t("mePage.enabled") : $t("mePage.disabled") }}</strong>
          </div>
        </div>
      </div>

      <!-- ENI 地址绑定弹窗 -->
      <transition name="fade">
        <div v-if="bindVisible" class="bind-mask" @click="closeBind"></div>
      </transition>

      <transition name="sheet-up">
        <div v-if="bindVisible" class="bind-sheet">
          <div class="bind-sheet-handle"></div>

          <div class="bind-sheet-head">
            <div>
              <div class="bind-sheet-title">
                {{ user.eniAddressBound ? $t("mePage.modifyEniAddress") : $t("mePage.bindEniAddress") }}
              </div>
              <div class="bind-sheet-desc">{{ $t("mePage.bindSheetDesc") }}</div>
            </div>
            <div class="bind-close" @click="closeBind">{{ $t("mePage.close") }}</div>
          </div>

          <div class="bind-form">
            <div class="bind-input-label">{{ $t("mePage.eniAddressLabel") }}</div>
            <div class="bind-input-wrap">
              <input
                v-model.trim="eniAddressInput"
                class="bind-input"
                type="text"
                :placeholder="$t('mePage.eniAddressPlaceholder')"
              />
            </div>

            <div class="bind-form-tip">
              {{ $t("mePage.bindFormTip") }}
            </div>

            <div class="bind-form-actions">
              <div class="action-btn ghost" @click="closeBind">{{ $t("mePage.cancel") }}</div>
              <div class="action-btn primary" :class="{ disabled: savingEni }" @click="submitBindEniAddress">
                {{ savingEni ? $t("mePage.saving") : $t("mePage.confirmSave") }}
              </div>
            </div>
          </div>
        </div>
      </transition>

      <AppFooter />
    </div>
  </div>
</template>

<script>
import AppHeader from '@/components/AppHeader.vue'
import AppFooter from '@/components/AppFooter.vue'
import wallet from '@/store/wallet'
import { fetchAppMe, updateEniAddress } from '@/api/overview'

const pageCache = {
  authKey: '',
  data: null,
  inflight: null
}

function readStorageToken() {
  const keys = ['token', 'access_token', 'Access-Token', 'Authorization', 'AUTH_TOKEN']

  for (let i = 0; i < keys.length; i++) {
    const key = keys[i]
    const localVal = window.localStorage.getItem(key)
    if (localVal) return localVal

    const sessionVal = window.sessionStorage.getItem(key)
    if (sessionVal) return sessionVal
  }

  return ''
}

function extractResponseData(res) {
  if (!res) return {}
  if (res.data && typeof res.data === 'object' && !Array.isArray(res.data)) {
    return res.data
  }
  return res
}

function toSafeNumber(val) {
  if (val === null || val === undefined || val === '') return 0
  const n = Number(val)
  return Number.isNaN(n) ? 0 : n
}

export default {
  name: 'MePage',
  components: {
    AppHeader,
    AppFooter
  },
  data() {
    return {
      walletPoller: null,
      lastAddr: '',
      lastConn: null,
      lastAuthKey: '',

      loading: false,
      loaded: false,
      errorMsg: '',

      bindVisible: false,
      eniAddressInput: '',
      savingEni: false,

      walletConnected: false,
      walletAddress: '',

      user: this.createEmptyUser()
    }
  },
  computed: {
    walletAddressShort() {
      if (!this.walletAddress) return '--'
      return this.shortAddress(this.walletAddress)
    },
    isLoggedIn() {
      return !!this.getAuthKey()
    },
    profileFirstLetter() {
      if (!this.walletAddress) return 'G'
      return String(this.walletAddress).replace(/^0x/i, '').slice(0, 1).toUpperCase() || 'G'
    },
    inviteCode() {
      return this.walletAddress || ''
    },
    inviteCodeShort() {
      return this.inviteCode ? this.shortAddress(this.inviteCode) : ''
    },
    inviteLink() {
      if (!this.inviteCode) return ''
      const origin = window.location.origin
      return `${origin}/#/bind-relation?ref=${this.inviteCode}`
    },
    inviteLinkDisplay() {
      return this.inviteLink || '--'
    }
  },
  mounted() {
    this.bindEvents()
    this.syncWalletFromStore()

    if (this.isLoggedIn) {
      this.loadMeHome(true)
    } else {
      this.resetPageState()
    }

    this.walletPoller = setInterval(() => {
      const st = wallet?.state || {}
      const addr = st.address || st.account || st.userAddress || ''
      const connected = !!st.connected || !!st.isConnected || (!!addr && addr.length > 0)
      const authKey = this.getAuthKey()

      if (addr !== this.lastAddr || connected !== this.lastConn) {
        this.lastAddr = addr
        this.lastConn = connected
        this.syncWalletFromStore()
      }

      if (authKey !== this.lastAuthKey) {
        this.lastAuthKey = authKey
        this.invalidateCache()

        if (authKey) {
          this.loadMeHome(true)
        } else {
          this.resetPageState()
        }
      }
    }, 400)
  },
  activated() {
    if (this.isLoggedIn) {
      this.loadMeHome(true)
    }
  },
  beforeDestroy() {
    if (this.walletPoller) clearInterval(this.walletPoller)
    this.unbindEvents()
    document.body.style.overflow = ''
  },
  methods: {
    createEmptyUser() {
      return {
        nickname: '',
        userCode: '',
        level: 'NO',
        usdtRate: '',
        directEniRate: '',
        poolQualified: false,
        sameLevelReward: '5%',
        overReward: '5%',
        eniAddressBound: false,
        eniAddress: '',
        directCount: 0,
        teamCount: 0
      }
    },

    resetPageState() {
      this.loading = false
      this.loaded = true
      this.errorMsg = ''
      this.user = this.createEmptyUser()
    },

    bindEvents() {
      window.addEventListener('auth:login', this.handleAuthLogin)
      window.addEventListener('auth:logout', this.handleAuthLogout)
    },

    unbindEvents() {
      window.removeEventListener('auth:login', this.handleAuthLogin)
      window.removeEventListener('auth:logout', this.handleAuthLogout)
    },

    async handleAuthLogin() {
      this.invalidateCache()
      this.syncWalletFromStore()
      await this.loadMeHome(true)
    },

    handleAuthLogout() {
      this.invalidateCache()
      this.user = this.createEmptyUser()
      this.loaded = true
      this.loading = false
      this.errorMsg = ''
    },

    invalidateCache() {
      pageCache.authKey = ''
      pageCache.data = null
      pageCache.inflight = null
    },

    getWalletAddress() {
      return (
        this.walletAddress ||
        wallet?.state?.address ||
        wallet?.state?.account ||
        wallet?.state?.walletAddress ||
        wallet?.state?.userAddress ||
        ''
      )
    },

    getAuthKey() {
      const token =
        wallet?.state?.token ||
        wallet?.state?.accessToken ||
        readStorageToken()

      const address = this.getWalletAddress()
      return [token, address].filter(Boolean).join('|')
    },

    syncWalletFromStore() {
      const st = wallet?.state || {}
      const addr = st.address || st.account || st.userAddress || ''
      const connected = !!st.connected || !!st.isConnected || (!!addr && addr.length > 0)

      this.walletConnected = connected
      this.walletAddress = addr || ''
    },

    normalizeMeData(raw) {
      const data = raw || {}
      return {
        nickname: data.nickname || '',
        userCode: data.userCode || '',
        level: data.level || 'NO',
        usdtRate: data.usdtRate || '',
        directEniRate: data.directEniRate || '',
        poolQualified: !!data.poolQualified,
        sameLevelReward: data.sameLevelReward || '5%',
        overReward: data.overReward || '5%',
        eniAddressBound: !!data.eniAddressBound,
        eniAddress: data.eniAddress || '',
        directCount: toSafeNumber(data.directCount),
        teamCount: toSafeNumber(data.teamCount)
      }
    },

    async loadMeHome(forceRefresh = false) {
      const authKey = this.getAuthKey()

      if (!authKey) {
        this.resetPageState()
        return
      }

      if (!forceRefresh && pageCache.data && pageCache.authKey === authKey) {
        this.user = this.normalizeMeData(pageCache.data)
        this.loaded = true
        this.errorMsg = ''
        return
      }

      if (!forceRefresh && pageCache.inflight && pageCache.authKey === authKey) {
        this.loading = true
        this.errorMsg = ''
        try {
          const cachedData = await pageCache.inflight
          this.user = this.normalizeMeData(cachedData)
          this.loaded = true
        } catch (e) {
          this.errorMsg = e?.message || this.$t('mePage.pageFetchFailed')
        } finally {
          this.loading = false
        }
        return
      }

      this.loading = true
      this.errorMsg = ''
      pageCache.authKey = authKey

      const request = fetchAppMe({ silent: true })
        .then((res) => {
          const payload = extractResponseData(res)
          pageCache.data = payload
          return payload
        })
        .finally(() => {
          pageCache.inflight = null
        })

      pageCache.inflight = request

      try {
        const payload = await request
        this.user = this.normalizeMeData(payload)
        this.loaded = true
      } catch (e) {
        this.errorMsg = e?.message || this.$t('mePage.pageFetchFailed')
        this.user = this.createEmptyUser()
        this.loaded = true
      } finally {
        this.loading = false
      }
    },

    formatInt(val) {
      return toSafeNumber(val).toLocaleString('en-US', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      })
    },

    shortAddress(addr) {
      if (!addr) return '--'
      if (addr.length <= 18) return addr
      return `${addr.slice(0, 6)}...${addr.slice(-4)}`
    },

    copyText(text, successText) {
      if (!text) return

      if (navigator.clipboard && window.isSecureContext) {
        navigator.clipboard.writeText(text).then(() => {
          this.toast(successText)
        }).catch(() => {
          this.fallbackCopy(text, successText)
        })
      } else {
        this.fallbackCopy(text, successText)
      }
    },

    fallbackCopy(text, successText) {
      const input = document.createElement('input')
      input.value = text
      document.body.appendChild(input)
      input.select()
      document.execCommand('copy')
      document.body.removeChild(input)
      this.toast(successText)
    },

    copyInviteLink() {
      if (!this.inviteLink) {
        this.toast(this.$t('mePage.noInviteLink'))
        return
      }
      this.copyText(this.inviteLink, this.$t('mePage.inviteLinkCopied'))
    },

    copyInviteCode() {
      if (!this.inviteCode) {
        this.toast(this.$t('mePage.noInviteCode'))
        return
      }
      this.copyText(this.inviteCode, this.$t('mePage.inviteCodeCopied'))
    },

    copyBoundEniAddress() {
      if (!this.user.eniAddressBound || !this.user.eniAddress) {
        this.toast(this.$t('mePage.noEniAddress'))
        return
      }
      this.copyText(this.user.eniAddress, this.$t('mePage.eniAddressCopied'))
    },

    handleBindEniAddress() {
      if (!this.isLoggedIn) {
        this.toast(this.$t('mePage.loginFirst'))
        return
      }
      if (this.savingEni) return

      this.eniAddressInput = this.user.eniAddressBound ? this.user.eniAddress : ''
      this.bindVisible = true
      document.body.style.overflow = 'hidden'
    },

    closeBind() {
      if (this.savingEni) return
      this.bindVisible = false
      this.eniAddressInput = ''
      document.body.style.overflow = ''
    },

    async submitBindEniAddress() {
      if (this.savingEni) return

      if (!this.eniAddressInput) {
        this.toast(this.$t('mePage.enterEniAddress'))
        return
      }

      if (!this.isLoggedIn) {
        this.toast(this.$t('mePage.loginFirst'))
        return
      }

      this.savingEni = true
      try {
        await updateEniAddress(this.eniAddressInput, { silent: true })

        this.user.eniAddress = this.eniAddressInput
        this.user.eniAddressBound = true

        this.invalidateCache()
        this.closeBind()
        this.toast(this.$t('mePage.eniAddressSaved'))
        await this.loadMeHome(true)
      } catch (e) {
        this.toast(e?.message || this.$t('mePage.eniAddressSaveFailed'))
      } finally {
        this.savingEni = false
      }
    },

    toast(message) {
      if (this.$toast) {
        if (typeof this.$toast === 'function') {
          this.$toast(message)
          return
        }
        if (typeof this.$toast.show === 'function') {
          this.$toast.show(message)
          return
        }
        if (typeof this.$toast.success === 'function') {
          this.$toast.success(message)
          return
        }
      }

      if (this.$message && typeof this.$message.success === 'function') {
        this.$message.success(message)
        return
      }

      alert(message)
    }
  }
}
</script>

<style lang="less" scoped>
.me-page {
  min-height: 100vh;
  background:
    radial-gradient(circle at top, rgba(88, 136, 255, 0.12) 0%, rgba(88, 136, 255, 0) 34%),
    linear-gradient(180deg, #f3f7ff 0%, #eef4ff 42%, #f8fbff 100%);
  color: #162033;
}

.page-body {
  padding: 12px 12px 28px;
}

.page-heading {
  padding: 8px 2px 2px;
}

.page-title {
  font-size: 30px;
  font-weight: 800;
  color: #142132;
  line-height: 1.2;
}

.page-desc {
  margin-top: 8px;
  font-size: 14px;
  color: #7d8aa0;
  line-height: 1.6;
}

.profile-card {
  position: relative;
  overflow: hidden;
  margin-top: 14px;
  border-radius: 30px;
  padding: 18px 16px 16px;
  background: linear-gradient(135deg, #165fff 0%, #3d84ff 46%, #66abff 100%);
  box-shadow: 0 22px 42px rgba(43, 111, 255, 0.25);
  color: #fff;
}

.profile-bg {
  position: absolute;
  pointer-events: none;
}

.profile-grid {
  inset: 0;
  background-image:
    linear-gradient(rgba(255, 255, 255, 0.045) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255, 255, 255, 0.045) 1px, transparent 1px);
  background-size: 22px 22px;
}

.profile-orb {
  border-radius: 50%;
  filter: blur(2px);
}

.profile-orb-1 {
  width: 140px;
  height: 140px;
  right: -30px;
  top: -26px;
  background: rgba(255, 255, 255, 0.13);
}

.profile-orb-2 {
  width: 100px;
  height: 100px;
  right: 18px;
  bottom: -24px;
  background: rgba(133, 226, 255, 0.12);
}

.profile-top,
.profile-main,
.profile-grid-info {
  position: relative;
  z-index: 2;
}

.profile-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.profile-user {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
}

.profile-avatar {
  width: 52px;
  height: 52px;
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.14);
  border: 1px solid rgba(255, 255, 255, 0.18);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  font-weight: 800;
  color: #fff;
  flex-shrink: 0;
}

.profile-name {
  font-size: 18px;
  font-weight: 800;
  color: #fff;
  word-break: break-word;
}

.profile-code {
  margin-top: 6px;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.74);
  word-break: break-all;
}

.profile-level {
  padding: 7px 12px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 800;
  border: 1px solid rgba(255, 255, 255, 0.2);
  background: rgba(255, 255, 255, 0.12);
  backdrop-filter: blur(10px);
  flex-shrink: 0;
}

.profile-main {
  margin-top: 18px;
}

.profile-main-label {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.74);
}

.profile-main-value {
  margin-top: 8px;
  font-size: 40px;
  font-weight: 800;
  line-height: 1;
  letter-spacing: -1px;
}

.profile-main-unit {
  margin-top: 8px;
  font-size: 14px;
  color: rgba(255, 255, 255, 0.78);
}

.profile-grid-info {
  margin-top: 18px;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
}

.profile-info-item {
  padding: 14px 12px;
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.14);
  backdrop-filter: blur(10px);
}

.profile-info-item span {
  display: block;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.72);
}

.profile-info-item strong {
  display: block;
  margin-top: 8px;
  font-size: 18px;
  color: #fff;
  line-height: 1.4;
  word-break: break-word;
}

.section-card {
  margin-top: 14px;
  background: rgba(255, 255, 255, 0.86);
  backdrop-filter: blur(16px);
  border-radius: 28px;
  padding: 18px 14px;
  border: 1px solid rgba(180, 205, 255, 0.45);
  box-shadow: 0 14px 34px rgba(104, 132, 182, 0.08);
}

.section-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}

.section-title {
  font-size: 24px;
  font-weight: 800;
  color: #162235;
  line-height: 1.2;
}

.section-desc {
  margin-top: 8px;
  font-size: 13px;
  color: #7c8aa0;
  line-height: 1.6;
}

.section-badge {
  padding: 8px 12px;
  border-radius: 999px;
  background: linear-gradient(135deg, #edf4ff, #f7faff);
  color: #2b7cff;
  font-size: 12px;
  font-weight: 700;
  white-space: nowrap;
}

.gold-badge {
  background: linear-gradient(135deg, #fff0d7, #fff7ea);
  color: #cf8b17;
}

.invite-panel {
  margin-top: 16px;
  padding: 16px 14px 14px;
  border-radius: 24px;
  background: linear-gradient(180deg, #f8fbff 0%, #f3f7ff 100%);
  border: 1px solid #e4edff;
}

.invite-panel-label {
  font-size: 13px;
  font-weight: 700;
  color: #72819a;
}

.invite-link-box {
  margin-top: 12px;
  min-height: 74px;
  border-radius: 18px;
  background: linear-gradient(180deg, #eef3fb 0%, #e9eff9 100%);
  border: 1px solid #d6e1f3;
  padding: 14px 14px;
  font-size: 14px;
  line-height: 1.6;
  color: #243246;
  word-break: break-all;
  display: flex;
  align-items: center;
}

.invite-actions {
  margin-top: 14px;
  display: flex;
  gap: 10px;
}

.invite-actions.dual .action-btn {
  flex: 1;
}

.invite-meta {
  margin-top: 14px;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.invite-pill {
  height: 34px;
  padding: 0 14px;
  border-radius: 999px;
  background: #edf2fa;
  color: #6b7b92;
  font-size: 13px;
  display: inline-flex;
  align-items: center;
  max-width: 100%;
}

.bind-card {
  margin-top: 16px;
  padding: 16px 14px;
  border-radius: 22px;
  background: linear-gradient(180deg, #f9fbff 0%, #f4f8ff 100%);
  border: 1px solid #e7efff;
}

.bind-status-row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}

.bind-label {
  font-size: 12px;
  color: #7f8ca2;
}

.bind-status {
  margin-top: 8px;
  display: inline-flex;
  height: 32px;
  padding: 0 12px;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 800;
  align-items: center;
  justify-content: center;
}

.bind-status.success {
  background: #e7f6ef;
  color: #08b57c;
}

.bind-status.pending {
  background: #fff5e7;
  color: #f59e0b;
}

.bind-tip {
  font-size: 12px;
  color: #8795ab;
  line-height: 1.7;
  text-align: right;
}

.bind-address-box {
  margin-top: 14px;
  padding: 14px 12px;
  border-radius: 18px;
  background: #fff;
  border: 1px solid #e7efff;
}

.bind-address-label {
  font-size: 12px;
  color: #7f8ca2;
}

.bind-address-value {
  margin-top: 8px;
  font-size: 14px;
  line-height: 1.7;
  color: #162234;
  word-break: break-all;
}

.bind-actions {
  margin-top: 14px;
  display: flex;
  gap: 10px;
}

.qualification-grid,
.account-list {
  margin-top: 16px;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
}

.qualification-item,
.account-item {
  padding: 14px 12px;
  border-radius: 18px;
  background: linear-gradient(180deg, #f9fbff 0%, #f4f8ff 100%);
  border: 1px solid #e7efff;
}

.qualification-item span,
.account-item span {
  display: block;
  font-size: 12px;
  color: #7f8ca2;
}

.qualification-item strong,
.account-item strong {
  display: block;
  margin-top: 8px;
  font-size: 16px;
  color: #162234;
  line-height: 1.5;
  word-break: break-word;
}

.action-btn {
  flex: 1;
  height: 42px;
  border-radius: 14px;
  font-size: 14px;
  font-weight: 800;
  display: flex;
  align-items: center;
  justify-content: center;
  user-select: none;
}

.action-btn.ghost {
  background: #edf4ff;
  color: #2b7cff;
}

.action-btn.primary {
  background: linear-gradient(135deg, #2a78ff, #5ea5ff);
  color: #fff;
  box-shadow: 0 12px 24px rgba(55, 123, 255, 0.18);
}

.action-btn.disabled {
  opacity: 0.55;
  pointer-events: none;
  box-shadow: none;
}

.bind-mask {
  position: fixed;
  inset: 0;
  background: rgba(8, 17, 36, 0.42);
  z-index: 90;
  backdrop-filter: blur(4px);
}

.bind-sheet {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 91;
  background: linear-gradient(180deg, #ffffff 0%, #f8fbff 100%);
  border-radius: 26px 26px 0 0;
  padding: 10px 14px 20px;
  max-height: 80vh;
  overflow-y: auto;
  box-shadow: 0 -18px 42px rgba(34, 63, 116, 0.18);
}

.bind-sheet-handle {
  width: 52px;
  height: 5px;
  border-radius: 999px;
  background: #d8e4f8;
  margin: 2px auto 12px;
}

.bind-sheet-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}

.bind-sheet-title {
  font-size: 22px;
  font-weight: 800;
  color: #162235;
  line-height: 1.2;
}

.bind-sheet-desc {
  margin-top: 8px;
  font-size: 13px;
  color: #7c8aa0;
  line-height: 1.6;
}

.bind-close {
  padding: 8px 12px;
  border-radius: 999px;
  background: #edf4ff;
  color: #2b7cff;
  font-size: 13px;
  font-weight: 800;
  white-space: nowrap;
}

.bind-form {
  margin-top: 16px;
}

.bind-input-label {
  font-size: 13px;
  font-weight: 700;
  color: #1a2538;
}

.bind-input-wrap {
  margin-top: 12px;
  border-radius: 18px;
  border: 1px solid #dbe8ff;
  background: linear-gradient(180deg, #f6faff 0%, #f2f7ff 100%);
  padding: 0 14px;
}

.bind-input {
  width: 100%;
  height: 52px;
  border: none;
  background: transparent;
  outline: none;
  font-size: 14px;
  color: #132038;
}

.bind-form-tip {
  margin-top: 10px;
  font-size: 12px;
  color: #8795ab;
  line-height: 1.7;
}

.bind-form-actions {
  margin-top: 16px;
  display: flex;
  gap: 10px;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.22s ease;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}

.sheet-up-enter-active,
.sheet-up-leave-active {
  transition: transform 0.26s ease, opacity 0.26s ease;
}

.sheet-up-enter,
.sheet-up-leave-to {
  transform: translateY(100%);
  opacity: 0.92;
}

@media (max-width: 375px) {

  .profile-grid-info,
  .qualification-grid,
  .account-list {
    grid-template-columns: 1fr;
  }

  .bind-status-row,
  .bind-sheet-head,
  .section-head {
    flex-direction: column;
    align-items: flex-start;
  }

  .bind-tip {
    text-align: left;
  }

  .profile-main-value {
    font-size: 34px;
  }

  .invite-actions {
    flex-direction: column;
  }
}

.profile-user-main {
  min-width: 0;
}

.profile-name {
  font-size: 18px;
  font-weight: 800;
  color: #fff;
  line-height: 1.25;
  word-break: break-word;
}

.profile-subtitle {
  margin-top: 6px;
  font-size: 12px;
  color: rgba(255,255,255,0.72);
  letter-spacing: 0.2px;
}

.account-item-wide {
  grid-column: span 2;
}

.account-item-wide strong {
  word-break: break-all;
  font-size: 15px;
  line-height: 1.6;
}

@media (max-width: 375px) {
  .account-item-wide {
    grid-column: span 1;
  }
}
</style>